import uniqBy from 'lodash.uniqby';

import { getErrorString } from './normalize-errors';
import { INVITATION_ACCEPTANCE_REQUIREMENT } from '../constants/contributor-assignments';
import getLogger from './logger';
import { accountAuthMethodExists } from './account';
import { normalizeEmail } from './email';

const log = getLogger('ContributorAssignments');

/**
 * Determines if a record is a ContributorAssignment type or
 * a ContributorAssignmentShare.
 * Returns redirect URL if succesful, or redirects user to error page.
 *
 * @param {Object} record - The record (ContributorAssignment or ContributorAssignmentShare).
 * @param {Object} record.account - The account related to the ContributorAssignment.
 * @returns {string|undefined} - Returns redirect URL if succesful, or redirects user to error page.
 */
const isContributorAssignmentShare = record => {
  return !record?.account;
};

/**
 * Determines if a contributor assignment/share is inherited or not.
 *
 * @param {Object} record - The record (ContributorAssignment or ContributorAssignmentShare).
 * @param {String} id - The ContributorAssignment or ContributorAssignmentShare id to check.
 * @returns {boolean} - Whether the contributor assignment/share is inherited or not.
 */
const isContributorAssignmentInherited = (record, id) => {
  const albumFolderId = record?.album_folder?.id;

  if (!albumFolderId) {
    return false;
  }

  return albumFolderId !== id;
};

const createContributorTree = (contributorHierarchy, rootEntityId) => {
  const roots = [];
  const lookup = {};

  contributorHierarchy.forEach(record => {
    const entityId = record.album?.id || record.album_folder?.id;
    const { album, album_folder: albumFolder, parent_id: parentId } = record;

    // 1. If we don't already have the albumFolder in lookup, add it.
    // Also create the structure necessary to hold its folder children
    if (!lookup[entityId]) {
      lookup[entityId] = albumFolder
        ? {
            ...albumFolder,
            children: {
              album_folders: {
                records: [],
              },
              albums: {
                records: [],
              },
            },
          }
        : album;
    }

    // 2. At this point we're adding the actual data of this folder to its entry
    // in the lookup table. We spread all of its data because
    // it might be a "dummy" folder we've created in step 3.
    const albumFolderRecords = [
      ...(albumFolder?.children?.album_folders?.records ?? []),
      ...(lookup[entityId].children?.album_folders?.records ?? []),
    ];

    if (albumFolder) {
      lookup[entityId] = {
        ...albumFolder,
        children: {
          albums: {
            ...(albumFolder.children?.albums ?? []),
            ...(lookup[entityId].children?.albums ?? []),
          },
          album_folders: {
            records: uniqBy(albumFolderRecords, 'id'),
          },
        },
      };
    }

    const item = lookup[entityId];

    if (!parentId) {
      roots.push(item);
    } else {
      // 3. If it's not a root folder AND we don't already have this folder
      // in lookup, we add a dummy object just to hold its children for now.
      // Later in the iteration, when we actually visit this folder we'll
      // spread all its actual data in step 2, along with the children we add now
      if (!lookup[parentId]) {
        lookup[parentId] = {
          children: {
            album_folders: {
              records: [],
            },
            albums: {
              records: [],
            },
          },
        };
      }
      // 4. Add the child folders to the dummy parent object we created above
      if (album) {
        lookup[parentId].children.albums.records = uniqBy(
          [item, ...lookup[parentId].children.albums.records],
          'id',
        );
      } else {
        lookup[parentId].children.album_folders.records = uniqBy(
          [item, ...lookup[parentId].children.album_folders.records],
          'id',
        );
      }
    }
  });

  return rootEntityId ? lookup[rootEntityId] : roots;
};

/**
 *
 * @param {Object} opts
 * @param {Object} opts.client The client.
 * @param {Object} opts.account The account.
 * @param {Object} opts.contributorInvitationDetails The ContributorInvitationDetails.
 * @param {function} opts.contributorAssignmentShareJoin The mutation.
 * @see README.md in this folder.
 */
const contributorInviteAccept = async ({
  client,
  account,
  contributorInvitationDetails,
  contributorAssignmentShareJoin,
}) => {
  const { email, phoneNumber, token } = contributorInvitationDetails;
  const { exists: emailExists, isVerified: emailIsVerified } = email
    ? await accountAuthMethodExists({ email, client })
    : {};

  const accountMismatch = email
    ? account.email_address &&
      // Both addresses are stored as they were typed, so they're normalized
      // before comparing them. Otherwise an invite sent to `Jane@Mail.com`
      // would not match the very same account under `jane@mail.com`.
      normalizeEmail(account.email_address) !== normalizeEmail(email) &&
      // For an unverified email, even if it's a mismatch, we're letting you through as per
      // Email invite flow #3.
      emailIsVerified
    : account.phone_number && account.phone_number !== phoneNumber;

  if (accountMismatch) {
    // Email flow #4
    // Phone flows #3
    log.error('contributorInviteAccept :: Account mismatch');
    const inviteLabel = email ? 'email address' : 'phone number';
    // The invitation was made to another account. Throw an error.
    throw new Error(
      `Your account does not match the invited ${inviteLabel}. Please log in or create an account with the provided ${inviteLabel}`,
    );
  }

  const { exists: phoneNumberExist } = phoneNumber
    ? await accountAuthMethodExists({ phoneNumber, client })
    : {};

  if (email && !account.email_address) {
    if (emailExists && emailIsVerified) {
      // Email flow #4
      log.info(
        `contributorInviteAccept :: Can't add email address to the account. Email exists on another account`,
      );

      throw new Error(
        'The email address in this invitation belongs to another account. If this is your account, please log in with it before accepting the invitation.',
      );
    }

    if (!emailExists) {
      // Email flow #2
      log.info(
        `contributorInviteAccept :: Adding email address: ${email} to account: ${account.id} `,
      );

      // Uncomment if we need to add the email using the FE. At the moment, email is
      // attached to the account in the platform when available. Be sure to update test for `Email flow #2`
      //
      // return INVITATION_ACCEPTANCE_REQUIREMENT.CONFIRM_EMAIL;
    }

    if (emailExists && !emailIsVerified) {
      log.info(
        `contributorInviteAccept :: Email exists, but its unverified. Accepting the invite...`,
      );
    }

    // If the email exists and is unverified, as per Email flow #3
    // we will accept the invitation, no user action is required.
  }

  if (phoneNumber && !account.phone_number) {
    if (phoneNumberExist) {
      // Phone flow #4
      log.info(
        `contributorInviteAccept :: Can't add phone number to the account. Phone number exists on another account.`,
      );

      throw new Error(
        'The phone number in this invitation belongs to another account. If this is your account, please log in with it before accepting the invitation.',
      );
    }

    // Phone flow #2:
    log.info(
      `contributorInviteAccept :: Adding phone number: ${phoneNumber} to account: ${account.id} `,
    );

    return INVITATION_ACCEPTANCE_REQUIREMENT.CONFIRM_PHONE_NUMBER;
  }

  log.info(`contributorInviteAccept :: accepting contribution state...`);
  // Phone flow #1
  // Email flows #1 & #3
  await contributorAssignmentShareJoin({
    // Pass on the accounts email / phone number so that we ensure
    // We pass the actual acceptance parameters.
    email: email || undefined,
    phoneNumber: phoneNumber || undefined,
    token,
  });

  return INVITATION_ACCEPTANCE_REQUIREMENT.NONE;
};

/**
 * Runs the contributor invite flow, accepting an invite, checking for proper credentials and the like.
 *
 * Whenever an invitationAcceptanceRequirement is returned, it means that there is further action for the user to
 * perform in order to accept the invite. Otherwise, either an error or a redirectUrl will be returned, meaning that there
 * is nothing more the user can do accept this invite or it was properly accepted.
 *
 * @param {Object} opts Options object.
 * @param {Object} opts.contributorInvitationDetails ContributorInvitationDetails object containing the invitee details (email, phone, token).
 * @param {Object} opts.account Current logged in account object.
 * @param {Function} opts.contributorAssignmentShareJoin GQL Mutation to join based on a ContributorShareAssignment.
 * @param {Object} opts.client Apollo client.
 * @param {Function} [opts.inviteAccept=contributorInviteAccept] Method that actually accepts the invite. Added as argument to make testing easier.
 * @returns {{ redirectUrl: string, error: string, invitationAcceptanceRequirement: INVITATION_ACCEPTANCE_REQUIREMENT}}
 */
const contributorInviteFlow = async ({
  account,
  contributorInvitationDetails,
  contributorAssignmentShareJoin,
  client,
  // Useful for injecting a test fn or something related.
  inviteAccept = contributorInviteAccept,
}) => {
  log.info(
    'contributorInviteFlow :: Starting flow with base account:',
    account,
  );

  try {
    const invitationAcceptanceRequirement = await inviteAccept({
      account,
      client,
      contributorInvitationDetails,
      contributorAssignmentShareJoin,
    });

    if (
      invitationAcceptanceRequirement !== INVITATION_ACCEPTANCE_REQUIREMENT.NONE
    ) {
      return {
        invitationAcceptanceRequirement,
        redirectUrl: null,
      };
    }

    return {
      redirectUrl: `/contributors/success`,
    };
  } catch (contributorInvitationError) {
    log.error(
      'contributorInviteFlow :: Found errors flow',
      contributorInvitationError,
    );

    return {
      error: getErrorString(contributorInvitationError),
    };
  }
};

/**
 * Based on a contributor assignment share, determines if the user
 * flow should be email-log in only, meaning no sign up at all or the option
 * to log in by phone.
 *
 * @param {Object} contributorAssignmentShare
 */
const isEmailLoginOnly = contributorAssignmentShare => {
  return (
    !!contributorAssignmentShare?.account?.email_address &&
    !contributorAssignmentShare?.account?.has_password &&
    !contributorAssignmentShare?.account?.phone_number
  );
};

const isAlbumContributor = contributorAssignment => {
  return !!contributorAssignment?.album?.id;
};

export {
  isContributorAssignmentShare,
  isContributorAssignmentInherited,
  createContributorTree,
  contributorInviteAccept,
  contributorInviteFlow,
  isEmailLoginOnly,
  isAlbumContributor,
};
